/**
 * Plan access hooks.
 *
 * Reads the org's billing tier to decide whether paid features are locked,
 * and lets a locked account ask the platform owner for information. The
 * request is sent by an Edge Function so the recipient address never reaches
 * the browser.
 */

import { useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/stores'
import { VENDOR_FREE_TIER, isPathAllowedOnVendorFree } from '@/lib/plan-access'

/** True when this org is on the free vendor tier, so paid pages are locked. */
export function useIsVendorFree(): boolean {
  const organization = useAuthStore((s) => s.organization)
  return organization?.billing_tier === VENDOR_FREE_TIER
}

/**
 * True when the current path should render the upgrade page instead of its
 * real content. Paid orgs never lock anything.
 */
export function useIsPlanLocked(pathname: string): boolean {
  const isVendorFree = useIsVendorFree()
  return isVendorFree && !isPathAllowedOnVendorFree(pathname)
}

export interface PlanUpgradeRequest {
  /** Human-readable feature name, e.g. "Estimates" */
  page?: string
  /** Path they were on, for context in the email */
  path?: string
}

/** Ask the platform owner for information about the locked features. */
export function useRequestPlanUpgrade() {
  return useMutation({
    mutationFn: async (input: PlanUpgradeRequest = {}) => {
      const { data, error } = await supabase.functions.invoke('request-plan-upgrade', {
        body: { page: input.page, path: input.path },
      })
      if (error) throw error
      if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error)
      return data as { success: true }
    },
  })
}
